/**
 * The wire protocol between the arena and its clients. Types and event names
 * only — the shapes both sides serialise, owned by the backend and imported
 * verbatim by the browser through `@shared`.
 */
import type { RewardBreakdown } from './rewards';
import type { DamageSnapshot, HitZone } from './damage';
import type { ShipCosmetics } from './liveries';
import type { LayoutId } from './islands';
import type { MatchMode } from './modes';
import type { ZoneId, ZoneSnapshot } from './objectives';
import type { ShipInput, TurretState } from './simulation';
import type { ShipClassId, ShipSpec, ShipSpecId } from './ship-spec';

export const BATTLE = {
  /** client → server */
  join: 'battle:join',
  input: 'battle:input',
  leave: 'battle:leave',
  /** server → client */
  welcome: 'battle:welcome',
  rejected: 'battle:rejected',
  snapshot: 'battle:snapshot',
  event: 'battle:event',
} as const;

export const SNAPSHOT_RATE = 20;
/** How far behind the newest snapshot other ships are drawn. */
export const INTERPOLATION_DELAY_MS = 100;

// ─── Teams and the match ─────────────────────────────────────────────────────
//
// Two teams, `A` and `B`. The server never says "ally": each client derives
// that from its own ship's team, so one snapshot serves both sides.

export type Team = 'A' | 'B';
export const TEAMS: readonly Team[] = ['A', 'B'];
/** Seats per team. */
export const TEAM_CAPACITY = 10;
export const ARENA_CAPACITY = TEAM_CAPACITY * 2;
/** Bots fill each team up to this many ships unless the arena is told otherwise. */
export const DEFAULT_TEAM_SIZE = 6;
/** @deprecated kept for the offline scene's option name; bots are per team now. */
export const DEFAULT_BOT_COUNT = DEFAULT_TEAM_SIZE;

/**
 * The match runs through these in order and loops. `waiting` holds until the
 * first human sits down; a `results` phase always ends in a fresh match.
 */
export type MatchPhase = 'waiting' | 'countdown' | 'battle' | 'results';
export const COUNTDOWN_SECONDS = 15;
export const BATTLE_SECONDS = 600;
export const RESULTS_SECONDS = 20;

/** The two teams spawn in lines this far apart, along x, facing each other. */
export const LINE_DISTANCE = 4000;
/** Metres between neighbouring ships in a spawn line. */
export const LINE_SPACING = 220;
/**
 * Half-size of the square of sea the match is fought on. Defined in
 * `simulation.ts`, which is what actually holds ships inside it, and
 * re-exported here so the wire's constants all read from one place.
 */
export { ARENA_HALF } from './simulation';

/** @deprecated no respawn inside a match any more; a sunk ship waits for the next one. */
export const RESPAWN_SECONDS = 8;
/** Legacy ring spawn radius, still used by the drift-free `waiting` layout. */
export const SPAWN_RADIUS = 900;

export interface TeamCount {
  alive: number;
  total: number;
}

export type TeamCounts = Record<Team, TeamCount>;

/** One row of the scoreboard broadcast at `results`. */
/**
 * How a row's damage was made, for the results screen's breakdown. Optional
 * on the wire: an older peer reads a row without it as a row with no story.
 */
export interface ScoreDetail {
  shellsFired: number;
  shellsHit: number;
  citadels: number;
  torpedoesFired: number;
  torpedoesHit: number;
  shellDamage: number;
  torpedoDamage: number;
  /** HP burnt off enemies by fires this captain lit; absent on an older peer. */
  fireDamage?: number;
  /** HP flooded out of enemies through holes this captain made. */
  floodDamage?: number;
  damageTaken: number;
  captureSeconds: number;
  /** Seconds afloat in the battle phase. */
  aliveSeconds: number;
}

export interface ScoreRow {
  id: string;
  name: string;
  bot: boolean;
  team: Team;
  specId: string;
  damage: number;
  kills: number;
  survived: boolean;
  credits: number;
  xp: number;
  /**
   * Lifetime battle XP as it stood when the ship joined — the scoreboard prints
   * the rank from it. Not the `xp` above, which is this battle's earnings.
   */
  rankXp: number;
  detail?: ScoreDetail;
  /** The credits line by line (`rewards.ts`); absent on older peers. */
  reward?: RewardBreakdown;
}

export interface MatchResult {
  /** The arena's per-match seed; doubles as a match identifier before the DB has one. */
  seed: number;
  /** What kind of battle it was and on which chart. Optional on the wire for an older peer. */
  mode?: MatchMode;
  layout?: LayoutId;
  /** Sea time the battle phase began and ended. */
  startedT: number;
  endedT: number;
  winner: Team | null;
  /**
   * Battle points at the final whistle (`objectives.ts`). Optional on the wire
   * because older peers and older stored results have none; a reader that
   * finds it missing is looking at a match fought before there was anything
   * to sail to.
   */
  scores?: Record<Team, number>;
  rows: ScoreRow[];
}

export interface JoinRequest {
  shipId?: ShipSpecId;
  /** The rules the captain wants to sail to (`modes.ts`); the server may pin another. */
  mode?: MatchMode;
}

/** One tick of player intent. `seq` is monotonic; the server echoes the last applied. */
export interface InputCommand extends ShipInput {
  seq: number;
}

export interface ShipSnapshot {
  id: string;
  name: string;
  bot: boolean;
  team: Team;
  /** A stock class for bots, the hull item id for a player's loadout. Display only. */
  specId: string;
  /** Which stock class (and so which hull model) this ship is built on. */
  classId: ShipClassId;
  x: number;
  z: number;
  yaw: number;
  speed: number;
  turrets: TurretState[];
  /**
   * Seconds left on each torpedo mount's reload. Absent on a ship that carries
   * no tubes, and optional on the wire for older peers.
   */
  torpedoMounts?: number[];
  hp: number;
  alive: boolean;
  /**
   * Whether the *other* team can see this ship right now (`vision.ts`).
   *
   * It travels for every ship rather than only the viewer's own, because it
   * costs a bit and it is the only way a client can tell "my team-mate is
   * being watched" from "my team-mate is merely in trouble". What the HUD does
   * with it is warn the player their own hull has been seen.
   */
  detected: boolean;
  /** The last input sequence the server applied for this ship (0 for bots). */
  lastInputSeq: number;
  /** This match's running tally. */
  kills: number;
  damage: number;
  /**
   * Lifetime battle XP, so the client can name the captain's rank itself
   * (`ranks.ts`). The number travels rather than the rank because the curve is
   * shared code: send the input, not the conclusion, and a retuned ladder needs
   * no server change. Zero for bots.
   */
  rankXp: number;
  /** Fires, flooding and the repair party (`damage.ts`). Optional on the wire for older peers. */
  condition?: DamageSnapshot;
  /**
   * Paint, ensign and the name on the hull (`liveries.ts`).
   *
   * In every snapshot rather than only in the welcome, because a ship that
   * joins mid-battle has to appear correctly painted to people who were
   * already here — and three short ids twenty times a second is cheaper than
   * the bookkeeping of a join message nobody may be listening for.
   */
  cosmetics?: ShipCosmetics;
}

export interface ShellSnapshot {
  id: number;
  x: number;
  y: number;
  z: number;
  /** Fired by the viewing team. Set per team view; absent on an unfiltered snapshot. */
  mine?: boolean;
}

/**
 * A torpedo in the water. No height: a fish runs at a fixed depth, so the
 * client draws its wake on the surface from two coordinates and a bearing.
 *
 * Unlike a shell, this is *filtered*. A tracer is a streak everyone can see;
 * a wake has to be spotted from a bridge, and `TORPEDO_SPOT_RANGE`
 * (`vision.ts`) decides which teams are told about which fish.
 */
export interface TorpedoSnapshot {
  id: number;
  x: number;
  z: number;
  yaw: number;
  /**
   * How fast it is running, m/s. The client cannot infer it from two
   * interpolated samples without inheriting their jitter, and it is what the
   * incoming-torpedo warning counts seconds with — so the tube's own number
   * travels rather than being guessed at the far end.
   */
  speed: number;
  /**
   * Launched by the viewer's own side. The client cannot work this out for
   * itself — the fish carries no owner on the wire, and it should not, since
   * that would name a ship the viewer may not be able to see — but which
   * wakes are *yours* is the difference between a chart that warns and a chart
   * that panics, so the one bit travels.
   */
  mine: boolean;
}

export interface BattleSnapshot {
  tick: number;
  /** Server sea time, seconds. Clients offset their clocks to this. */
  t: number;
  phase: MatchPhase;
  /** Sea time at which the current phase ends (Infinity while `waiting`). */
  phaseEndsT: number;
  /**
   * The rules this match is played to (`modes.ts`). Which rings are on the
   * chart and what the cap is are looked up from it, not sent.
   */
  mode: MatchMode;
  teams: TeamCounts;
  ships: ShipSnapshot[];
  shells: ShellSnapshot[];
  torpedoes: TorpedoSnapshot[];
  /**
   * The capture zones (`objectives.ts`). Not filtered by vision, unlike ships
   * and wakes: a ring is a place, drawn on the chart before the battle began,
   * and who holds it is the scoreboard. Hiding that would only hide the game.
   */
  zones: ZoneSnapshot[];
  /** Battle points, both teams. The race to the mode's `victoryPoints`. */
  scores: Record<Team, number>;
  /** The convoy's tally, on a mode that has one (`convoy.ts`); absent otherwise. */
  convoy?: ConvoySnapshot;
}

export interface ConvoySnapshot {
  total: number;
  /** Still at sea. */
  afloat: number;
  arrived: number;
  sunk: number;
}

export type BattleEvent =
  | { kind: 'fire'; shipId: string; turret: number; x: number; y: number; z: number }
  /**
   * A salvo of fish went over the side. Carries the launch point so the client
   * can put foam there; it reaches only teams that can see the ship that
   * launched — a launch is silent, and the wake is what gives it away later.
   */
  | { kind: 'torpedo_launch'; shipId: string; mount: number; x: number; z: number; yaw: number }
  /** `shipId` is the ship that was struck, `byId` the captain who launched. */
  | { kind: 'torpedo_hit'; shipId: string; byId: string; x: number; z: number; damage: number }
  /** A fish ran itself onto the shore instead of a hull: no warhead, no ship, just where it happened. */
  | { kind: 'torpedo_beached'; x: number; z: number; byId: string; team: Team }
  /**
   * A fish ran itself onto the shore instead of a hull. Names no ship — like
   * `splash`, it is visible to everyone regardless of who can see `byId`'s
   * ship, a wake ending on a beach being exactly as public as a column of
   * water.
   */
  | { kind: 'torpedo_beached'; x: number; z: number; byId: string; team: Team }
  /**
   * A round that found the water instead of a hull.
   *
   * `calibre` is the shell's own damage. It travels because the size of a
   * column is the only thing a fall of shot tells a gunner about whose salvo
   * it was, and the client has no other number to raise one from; `byId`
   * names the captain who fired, so a player can pick their own straddle out
   * of a sea with four ships shooting into it. Both optional on the wire:
   * an older peer simply gets an average column.
   */
  | { kind: 'splash'; x: number; z: number; calibre?: number; byId?: string; land?: boolean }
  /**
   * A round that found a hull. `zone` is the same classification the paired
   * `damage` event carries, repeated here because this is the event that owns
   * the impact's position — a citadel and a bow hit have to look different at
   * the point of contact, and pairing the two events by hand at the far end
   * would be guesswork about ordering.
   */
  | { kind: 'hit'; shipId: string; byId: string; x: number; y: number; z: number; damage: number; zone?: HitZone }
  | { kind: 'sunk'; shipId: string; byId: string }
  /** A merchant is home or on the bottom, and which team the points went to. */
  | { kind: 'convoy'; shipId: string; outcome: 'arrived' | 'sunk'; team: Team }
  /** `spec` rides along when the ship is a player's loadout, so late peers can render it. */
  | { kind: 'spawn'; shipId: string; spec?: ShipSpec }
  | { kind: 'left'; shipId: string }
  | { kind: 'phase'; phase: MatchPhase; endsT: number; seed: number; mode: MatchMode }
  | { kind: 'results'; result: MatchResult }
  /**
   * HP removed. `shell` carries the zone and the shooter; `fire` and `flood`
   * are damage over time, coalesced to one event per ship per second; `ram`
   * is a collision, and names the other ship without crediting it; `torpedo`
   * is a warhead below the waterline and is credited like a shell.
   */
  | { kind: 'damage'; shipId: string; byId?: string; amount: number; zone?: HitZone; source: 'shell' | 'torpedo' | 'fire' | 'flood' | 'ram'; x: number; z: number }
  | { kind: 'fire_started'; shipId: string }
  | { kind: 'fire_out'; shipId: string }
  | { kind: 'flood_started'; shipId: string }
  | { kind: 'flood_out'; shipId: string }
  | { kind: 'repair'; shipId: string }
  /** A zone changed hands, or went neutral (`owner: null`). */
  | { kind: 'zone_owner'; zone: ZoneId; owner: Team | null }
  /** Somebody started working a zone, or stopped (`team: null`). */
  | { kind: 'zone_capture'; zone: ZoneId; team: Team | null };

export interface WelcomeMessage {
  yourId: string;
  /** The spec the server built for you (from your shipyard loadout). */
  spec: ShipSpec;
  /** Full specs of every ship present whose spec is not a stock class, by ship id. */
  specs: Record<string, ShipSpec>;
  team: Team;
  phase: MatchPhase;
  phaseEndsT: number;
  /**
   * The match seed, so a captain who joins mid-match draws the same land and
   * weather as everyone else without waiting for the next `phase` event.
   * Optional on the wire for an older server; the client then draws open sea
   * and the clear day until the next phase.
   */
  seed?: number;
  /** The mode, for the same reason as the seed. Optional for an older server: then `domination`. */
  mode?: MatchMode;
  tick: number;
  t: number;
  tickRate: number;
  ships: ShipSnapshot[];
}

export interface RejectedMessage {
  reason: string;
}
